import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setPageMeta } from '../utils/seo';
import { Megaphone, CheckCircle, UploadSimple } from '@phosphor-icons/react';
import { openCheckout } from '../utils/checkout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdvertisePage() {
  const [params] = useSearchParams();
  const [placements, setPlacements] = useState([]);
  const [trades, setTrades] = useState([]);
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({ company_name: '', email: '', target_url: '', placement: 'homepage', weeks: 2, target_trade: '', target_city: '' });
  const [imageData, setImageData] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(null);
  const [statsToken, setStatsToken] = useState(null);

  useEffect(() => {
    setPageMeta({
      title: 'Advertise on ConstructHelp — Reach Homeowners & Contractors',
      description: 'Buy ad space on ConstructHelp: homepage banners, cost guides, and hire pages seen by homeowners actively planning construction projects.',
      url: `${window.location.origin}/advertise`,
    });
    axios.get(`${API}/api/ads/placements`).then((r) => { setPlacements(r.data.placements); setTrades(r.data.trades || []); }).catch(() => {});
    axios.get(`${API}/api/ads/availability`).then((r) => setAvail(r.data)).catch(() => {});
    const sid = params.get('session_id'), adId = params.get('ad_id');
    if (params.get('ad') === 'success' && sid && adId) {
      axios.post(`${API}/api/ads/verify?session_id=${sid}&ad_id=${adId}`)
        .then((r) => { setVerified(r.data.status); setStatsToken(r.data.stats_token || null); })
        .catch(() => setVerified('error'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { setError('Image must be under 500KB'); return; }
    const reader = new FileReader();
    reader.onload = () => { setImageData(reader.result); setError(''); };
    reader.readAsDataURL(f);
  };

  const price = () => {
    const p = placements.find((x) => x.id === form.placement);
    if (!p) return '—';
    const weekly = form.target_trade && !form.target_city && p.nationwide_weekly_price ? p.nationwide_weekly_price : p.weekly_price;
    return (weekly * form.weeks).toFixed(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!imageData) { setError('Upload your banner image (1200×200 recommended)'); return; }
    setStatus('loading');
    setError('');
    try {
      const r = await axios.post(`${API}/api/ads/create`, { ...form, weeks: Number(form.weeks), image_data: imageData });
      openCheckout(r.data.checkout_url);
    } catch (err) {
      setStatus('idle');
      setError(err.response?.data?.detail || 'Something went wrong');
    }
  };

  const inputCls = 'w-full h-12 px-3 bg-white border-2 border-black rounded-none font-medium focus:outline-none focus:ring-2 focus:ring-[#D95800]';

  if (verified === 'pending_approval' || verified === 'active') {
    return (
      <div className="min-h-screen bg-[#E8E9EB] flex items-center justify-center px-4" data-testid="ad-success-page">
        <div className="bg-white border-2 border-black p-10 text-center shadow-[6px_6px_0px_#D95800] max-w-lg">
          <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-600 mb-4" />
          <h1 className="text-2xl font-black uppercase mb-2">Payment received!</h1>
          <p className="text-neutral-600 mb-6">Your ad is now in review. Our team approves most ads within 24 hours — you'll see it live right after. Questions? support@constructhelp.com</p>
          {statsToken && (
            <div className="bg-orange-50 border-2 border-[#D95800] p-4 mb-6 text-left" data-testid="ad-stats-link-box">
              <p className="text-xs font-black uppercase text-[#D95800] mb-1">Bookmark your stats dashboard</p>
              <p className="text-sm text-neutral-600 mb-2">Track your impressions and clicks anytime — no login needed:</p>
              <Link to={`/ad-stats/${statsToken}`} className="font-bold text-sm text-[#D95800] underline break-all" data-testid="ad-stats-link">
                {window.location.origin}/ad-stats/{statsToken}
              </Link>
            </div>
          )}
          <Link to="/" className="inline-flex h-11 px-6 items-center bg-[#D95800] text-white font-black uppercase border-2 border-black">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB]" data-testid="advertise-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="w-14 h-14 bg-[#D95800] border-2 border-white flex items-center justify-center mb-5">
            <Megaphone size={30} weight="fill" className="text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white uppercase leading-tight mb-3">Advertise on ConstructHelp</h1>
          <p className="text-neutral-300 max-w-2xl">Put your brand in front of homeowners actively planning projects and pros looking for tools and materials. Upload a banner, pick your spot, go live after a quick review.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="text-lg font-black uppercase mb-4">Ad spots & pricing</h2>
          <div className="space-y-3">
            {placements.map((p) => (
              <label key={p.id} className={`flex items-center justify-between p-4 bg-white border-2 cursor-pointer ${form.placement === p.id ? 'border-[#D95800] shadow-[3px_3px_0px_#D95800]' : 'border-black'}`} data-testid={`placement-${p.id}`}>
                <span className="flex items-center gap-3">
                  <input type="radio" name="placement" checked={form.placement === p.id}
                    onChange={() => setForm({ ...form, placement: p.id, target_trade: '', target_city: '' })} className="accent-[#D95800] w-4 h-4" />
                  <span className="font-black">{p.label}{p.trade_targetable && <span className="block text-[11px] font-bold text-neutral-500 normal-case">Target a single trade — exclusive to your category</span>}</span>
                </span>
                <span className="font-black text-[#D95800]">
                  {p.regular_weekly_price && (
                    <span className="text-neutral-400 line-through font-bold text-sm mr-1.5" data-testid={`regular-price-${p.id}`}>${p.regular_weekly_price}</span>
                  )}
                  ${p.weekly_price}/wk
                  {p.regular_weekly_price && (
                    <span className="block text-[9px] font-black uppercase tracking-wider text-emerald-700 text-right" data-testid={`intro-badge-${p.id}`}>Intro price</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <div className="mt-6 bg-white border-2 border-black p-5" data-testid="ad-placement-preview">
            <p className="text-xs font-black uppercase mb-3">Where your ad appears</p>
            <img src={`/ad-previews/${form.placement}.jpg`} alt={`Screenshot showing the ${form.placement} ad placement on ConstructHelp`}
              className="w-full border-2 border-neutral-900" loading="lazy" data-testid={`ad-preview-img-${form.placement}`} />
            <p className="text-[11px] font-bold text-neutral-500 mt-2">Live screenshot — the green box marks your banner spot on the {placements.find((x) => x.id === form.placement)?.label || 'selected'} placement.</p>
          </div>
          <div className="mt-6 bg-white border-2 border-black p-5" data-testid="ad-slot-map">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-black uppercase">Trade slot availability</p>
              {avail && (
                <span className="text-[11px] font-black uppercase text-neutral-500">Hire-a-Pro exclusives</span>
              )}
            </div>
            {!avail ? (
              <p className="text-sm text-neutral-400">Loading…</p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                {avail.trades.map((t) => (
                  <div key={t.id}
                    className={`flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold border ${t.taken ? 'bg-neutral-100 border-neutral-300 text-neutral-400' : 'bg-emerald-50 border-emerald-300 text-emerald-800'}`}
                    data-testid={`slot-${t.id}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${t.taken ? 'bg-neutral-400' : 'bg-emerald-500'}`} />
                    <span className="truncate">{t.label}</span>
                    <span className="ml-auto shrink-0 uppercase text-[9px] font-black">{t.taken ? `til ${t.until?.slice(5, 10)}` : 'Open'}</span>
                  </div>
                ))}
              </div>
            )}
            {avail && avail.trade_slots_left <= avail.trade_slots_total && (
              <p className="text-[11px] font-bold text-neutral-500 mt-2">One advertiser per trade — once a slot is booked it's locked until the run ends.</p>
            )}
          </div>
          <div className="mt-6 bg-neutral-900 text-white p-5 border-2 border-black">
            <p className="text-xs font-black uppercase text-neutral-400 mb-2">What you get</p>
            <ul className="text-sm space-y-1.5">
              <li>• Banner shown to real visitors (1200×200 recommended, JPG/PNG under 500KB)</li>
              <li>• Clear "Sponsored" labeling — builds trust with our audience</li>
              <li>• Approval within 24 hours, live immediately after</li>
              <li>• Secure card payment via Stripe</li>
            </ul>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border-2 border-black p-6 shadow-[5px_5px_0px_#D95800] space-y-4 h-fit" data-testid="ad-form">
          <input required placeholder="Company name *" value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inputCls} data-testid="ad-company" />
          <input required type="email" placeholder="Billing email *" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="ad-email" />
          <input required placeholder="Destination URL (https://...) *" value={form.target_url}
            onChange={(e) => setForm({ ...form, target_url: e.target.value })} className={inputCls} data-testid="ad-url" />
          {placements.find((x) => x.id === form.placement)?.trade_targetable && (
            <div data-testid="ad-trade-picker">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-black uppercase text-neutral-500">Trade targeting</label>
                {avail && (
                  <span className={`text-xs font-black uppercase ${avail.trade_slots_left <= 3 ? 'text-red-600' : 'text-emerald-700'}`} data-testid="ad-slots-left">
                    {avail.trade_slots_left} of {avail.trade_slots_total} exclusive slots left
                  </span>
                )}
              </div>
              <select value={form.target_trade} onChange={(e) => setForm({ ...form, target_trade: e.target.value, target_city: '' })}
                className={inputCls} data-testid="ad-trade-select">
                <option value="">All Hire-a-Pro pages (run of section)</option>
                {trades.map((t) => {
                  const a = avail?.trades?.find((x) => x.id === t.id);
                  const taken = a?.taken;
                  const citiesBooked = a ? Object.keys(a.cities_taken || {}).length : 0;
                  return (
                    <option key={t.id} value={t.id} disabled={taken}>
                      {t.label} pages only — {taken ? `booked until ${a.until?.slice(0, 10)}` : citiesBooked > 0 ? `${citiesBooked} cit${citiesBooked > 1 ? 'ies' : 'y'} booked` : 'exclusive'}
                    </option>
                  );
                })}
              </select>
              {form.target_trade && (() => {
                const a = avail?.trades?.find((x) => x.id === form.target_trade);
                const citiesTaken = a?.cities_taken || {};
                const hasCityBookings = Object.keys(citiesTaken).length > 0;
                const p = placements.find((x) => x.id === form.placement);
                const nwPrice = p?.nationwide_weekly_price;
                return (
                  <div className="mt-2" data-testid="ad-city-picker">
                    <label className="block text-xs font-black uppercase text-neutral-500 mb-1">City targeting (optional)</label>
                    <select value={form.target_city} onChange={(e) => setForm({ ...form, target_city: e.target.value })}
                      className={inputCls} data-testid="ad-city-select">
                      <option value="" disabled={hasCityBookings}>
                        Nationwide — every {trades.find((t) => t.id === form.target_trade)?.label} page{nwPrice ? ` · $${nwPrice}/wk` : ''}{hasCityBookings ? ' (unavailable — city slots booked)' : ''}
                      </option>
                      {(avail?.cities || []).map((c) => {
                        const until = citiesTaken[c.id];
                        return (
                          <option key={c.id} value={c.id} disabled={!!until}>
                            {c.label} only — {until ? `booked until ${until.slice(0, 10)}` : `exclusive · $${p?.weekly_price}/wk`}
                          </option>
                        );
                      })}
                    </select>
                    {!form.target_city && nwPrice && (
                      <p className="text-xs font-bold text-neutral-500 mt-1" data-testid="ad-nationwide-note">
                        Nationwide exclusivity covers all {trades.find((t) => t.id === form.target_trade)?.label} pages in every city — premium rate ${nwPrice}/wk. Pick a single city for ${p?.weekly_price}/wk.
                      </p>
                    )}
                  </div>
                );
              })()}
              {form.target_trade && (
                <p className="text-xs text-neutral-500 mt-1" data-testid="ad-trade-note">
                  Your banner gets priority on the {trades.find((t) => t.id === form.target_trade)?.label} hire page.
                </p>
              )}
            </div>
          )}
          <div>
            <label className="flex items-center gap-2 h-12 px-3 border-2 border-dashed border-black cursor-pointer font-bold text-sm hover:bg-orange-50" data-testid="ad-image-label">
              <UploadSimple size={18} weight="bold" />
              {imageData ? 'Banner uploaded ✓ (click to replace)' : 'Upload banner image *'}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onImage} className="hidden" data-testid="ad-image-input" />
            </label>
            {imageData && <img src={imageData} alt="Banner preview" className="mt-2 w-full max-h-28 object-cover border-2 border-neutral-200" data-testid="ad-image-preview" />}
          </div>
          <div className="flex items-center gap-3">
            <label className="font-bold text-sm">Duration:</label>
            <select value={form.weeks} onChange={(e) => setForm({ ...form, weeks: e.target.value })}
              className="h-11 px-2 border-2 border-black font-bold" data-testid="ad-weeks">
              {[1, 2, 4, 8, 12].map((w) => <option key={w} value={w}>{w} week{w > 1 ? 's' : ''}</option>)}
            </select>
            <span className="ml-auto font-black text-xl text-[#D95800]" data-testid="ad-total">${price()}</span>
          </div>
          {error && <p className="text-sm font-bold text-red-600" data-testid="ad-error">{error}</p>}
          <button type="submit" disabled={status === 'loading'}
            className="w-full h-13 py-3 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] disabled:opacity-60"
            data-testid="ad-submit">
            {status === 'loading' ? 'Redirecting to payment…' : `Pay $${price()} & Submit for Review`}
          </button>
        </form>
      </div>
    </div>
  );
}
